/**
 * 底部面板（iOS Sheet）。
 *
 * 三件事凑齐才算"像系统的"：
 *   1. 上滑用 expo.out，快到位的减速；退场比入场快，这是系统的习惯
 *   2. 拖拽关闭：往下拖时面板跟手，往上拖有阻尼（不能拖出屏幕外）
 *   3. 松手时按**位移 + 速度**两个条件决定吸附到哪，
 *      只按位移会导致"快速甩一下但没拖够"关不掉，手感很假
 *
 * 只动 transform 和 opacity。拖拽期间用 quickSetter 直接写样式，
 * 不走 React 状态，否则每帧重渲染必掉帧。
 */

import { useEffect, useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

/** 拖过这个距离就关闭 */
const DISMISS_DISTANCE = 108;
/** 或者：甩得够快也关闭（px/ms） */
const DISMISS_VELOCITY = 0.55;
/** 往上拖的阻尼系数，越小越拖不动 */
const UPWARD_RESISTANCE = 0.18;

export interface UseSheetOptions {
	open: boolean;
	/**
	 * DOM 是否真的存在。必须传进来，不能只看 open。
	 *
	 * 踩过的坑：Sheet 组件在 open=false 时就已挂载，此时 panel 还没渲染、ref 是 null。
	 * 如果动画只依赖 [open]，那么 open 翻成 true 的那一次 effect 会因为 ref 为 null
	 * 提前返回，而之后 ref 挂上时 effect 不再重跑 —— 面板永远停在屏幕外。
	 * 所以依赖里必须带上"DOM 已存在"这个事实。
	 */
	mounted: boolean;
	/** 用户要求关闭（拖拽关闭 / 点遮罩 / Esc） */
	onClose: () => void;
	/** 退场动画播完，可以真正卸载了 */
	onExited: () => void;
}

export interface SheetControls {
	sheetRef: RefObject<HTMLDivElement>;
	backdropRef: RefObject<HTMLDivElement>;
}

export function useSheet({ open, mounted, onClose, onExited }: UseSheetOptions): SheetControls {
	const sheetRef = useRef<HTMLDivElement | null>(null);
	const backdropRef = useRef<HTMLDivElement | null>(null);
	const onCloseRef = useRef(onClose);
	const onExitedRef = useRef(onExited);

	onCloseRef.current = onClose;
	onExitedRef.current = onExited;

	/* ---------------- 入场 / 退场 ---------------- */

	useEffect(() => {
		const sheet = sheetRef.current;
		const backdrop = backdropRef.current;
		if (!sheet || !backdrop) return;

		if (prefersReducedMotion()) {
			gsap.set(sheet, { yPercent: open ? 0 : 100 });
			gsap.set(backdrop, { autoAlpha: open ? 1 : 0 });
			if (!open) onExitedRef.current();
			return;
		}

		if (open) {
			gsap.set(sheet, { yPercent: 0, y: 0 });
			gsap.fromTo(
				sheet,
				{ yPercent: 100, y: 0 },
				{ yPercent: 0, y: 0, duration: MOTION.sheetIn, ease: 'expo.out' },
			);
			gsap.fromTo(
				backdrop,
				{ autoAlpha: 0 },
				{ autoAlpha: 1, duration: MOTION.sheetIn * 0.6, ease: 'power2.out' },
			);
		} else {
			// 退场比入场快：拖沓的关闭会让人以为卡住了
			gsap.to(sheet, {
				yPercent: 100,
				y: 0,
				duration: 0.28,
				ease: 'power3.in',
				onComplete: () => onExitedRef.current(),
			});
			gsap.to(backdrop, { autoAlpha: 0, duration: 0.2, ease: 'power2.in' });
		}
	}, [mounted, open]);

	/* ---------------- Esc 关闭 ---------------- */

	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') onCloseRef.current();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [open]);

	/* ---------------- 拖拽关闭 ---------------- */

	useEffect(() => {
		const sheet = sheetRef.current;
		const backdrop = backdropRef.current;
		if (!sheet || !backdrop) return;

		const backdropSetter = gsap.quickSetter(backdrop, 'opacity') as (value: number) => void;

		let dragging = false;
		let startY = 0;
		let lastY = 0;
		let lastTime = 0;
		let velocity = 0;
		let offset = 0;

		const beginDrag = (event: PointerEvent): void => {
			dragging = true;
			startY = event.clientY;
			lastY = event.clientY;
			lastTime = performance.now();
			velocity = 0;
			offset = 0;
			gsap.killTweensOf(sheet);
			gsap.set(sheet, { y: 0 });
		};

		const moveDrag = (event: PointerEvent): void => {
			if (!dragging) return;
			const raw = event.clientY - startY;
			// 往上拖阻尼：面板不该被拖到屏幕上方去
			offset = raw > 0 ? raw : raw * UPWARD_RESISTANCE;

			const now = performance.now();
			const elapsed = now - lastTime;
			if (elapsed > 0) velocity = (event.clientY - lastY) / elapsed;
			lastY = event.clientY;
			lastTime = now;

			gsap.set(sheet, { y: offset });

			const height = sheet.offsetHeight || window.innerHeight;
			backdropSetter(Math.max(0, 1 - offset / height));
		};

		const endDrag = (): void => {
			if (!dragging) return;
			dragging = false;

			const shouldDismiss = offset > DISMISS_DISTANCE || velocity > DISMISS_VELOCITY;

			if (shouldDismiss) {
				// 用当前速度接着往下飞，比固定时长自然
				const height = sheet.offsetHeight || window.innerHeight;
				gsap.to(sheet, {
					y: height,
					duration: 0.24,
					ease: 'power2.in',
					onComplete: () => {
						gsap.set(sheet, { yPercent: 100, y: 0 });
						onCloseRef.current();
					},
				});
				gsap.to(backdrop, { opacity: 0, duration: 0.22, ease: 'power2.in' });
				return;
			}

			// 吸附回原位，带一点回冲
			gsap.to(sheet, {
				y: 0,
				duration: MOTION.sheetBack,
				ease: MOTION.ease.back,
				overwrite: 'auto',
				onComplete: () => gsap.set(sheet, { clearProps: 'transform' }),
			});
			gsap.to(backdrop, { opacity: 1, duration: 0.24, ease: 'power2.out' });
		};

		const onPointerDown = (event: PointerEvent): void => {
			// 只响应左键 / 触摸
			if (event.button !== 0 && event.pointerType === 'mouse') return;
			beginDrag(event);
			window.addEventListener('pointermove', moveDrag);
			window.addEventListener('pointerup', endDrag);
			window.addEventListener('pointercancel', endDrag);
		};

		const grab = sheet.querySelector<HTMLElement>('[data-sheet-grab]');
		grab?.addEventListener('pointerdown', onPointerDown);

		return () => {
			grab?.removeEventListener('pointerdown', onPointerDown);
			window.removeEventListener('pointermove', moveDrag);
			window.removeEventListener('pointerup', endDrag);
			window.removeEventListener('pointercancel', endDrag);
			gsap.killTweensOf(sheet);
			gsap.killTweensOf(backdrop);
		};
		// 同样必须依赖 mounted：拖拽把手只在 DOM 存在时才拿得到
	}, [mounted, open]);

	/**
	 * 拖拽把手必须带 `data-sheet-grab` 属性，监听在 effect 里直接绑到它身上。
	 * 用属性而不是 props 回调，是为了让拖拽逻辑（含 window 级 move/up 监听、
	 * 速度采样、清理）完整地关在这个文件里，组件只负责给个挂载点。
	 */
	return { sheetRef, backdropRef };
}
